import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

import { selectDetailsRollout } from "@dashboard/devices/store";
import { Paper, TableContainer, TableRow } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { CustomTableHeader } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { useGetRolloutsByBuildQuery } from "../../api";
import { Rollout } from "../../types";

import { columns, defaultParams } from "./RolloutTable.utils";

type Props = {
    codename: string | null;
    workspaceId: number;
};

export const RolloutTable = (props: Props) => {
    const [params, setParams] = React.useState(defaultParams);
    const { data: rolloutData, isFetching } = useGetRolloutsByBuildQuery(
        props.codename ? { codename: props.codename, workspaceId: props.workspaceId, ...params } : skipToken,
    );
    const data = React.useMemo(() => rolloutData?.list ?? [], [rolloutData]);

    const { t } = useTranslation(["pages"]);

    const details = useSelector(selectDetailsRollout);

    const onSort = (id: string) => {
        setParams((prev) => ({ ...prev, sortBy: id, sortDescending: !prev.sortDescending, pageIndex: 0 }));
    };

    const rolloutTable = useReactTable<Rollout>({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    React.useEffect(() => {
        setParams(defaultParams);
    }, [props.codename, props.workspaceId]);

    return (
        <React.Fragment>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {rolloutTable.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <CustomTableHeader
                                            key={header.id}
                                            sortCriteria={params.sortBy}
                                            sortDirection={params.sortDescending ? "desc" : "asc"}
                                            onSort={onSort}
                                            {...header}
                                        />
                                    );
                                })}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>

                    <TableBody noDataMessage={t("pages:rolloutsPage.noRollouts")} table={rolloutTable} loading={isFetching}>
                        {rolloutTable.getRowModel().rows.map((row) => (
                            <HoverRow {...row} selected={details?.selectedId === row.original.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <React.Fragment key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </React.Fragment>
                                ))}
                            </HoverRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <CustomTablePagination
                count={rolloutData?.count ?? 0}
                rowsPerPage={params.pageSize}
                page={params.pageIndex}
                onPageChange={(_event, page) => setParams({ ...params, pageIndex: page })}
                onRowsPerPageChange={(e) => setParams({ ...params, pageSize: Number(e.target.value), pageIndex: 0 })}
            />
        </React.Fragment>
    );
};
